import { Coffee, BookOpen, Briefcase, Video, Users, Timer, BookCheck, UsersRound, CheckSquare, Bell } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const modes = [
  {
    id: "chill",
    title: "Chill",
    description: "Relax and connect with others in a comfortable environment",
    icon: Coffee,
    color: "hsl(328 85% 55%)",
    features: [
      { icon: Video, label: "Stream" },
      { icon: Users, label: "Friends" },
    ],
  },
  {
    id: "study",
    title: "Study",
    description: "Focus on learning with structured tools and accountability",
    icon: BookOpen,
    color: "hsl(193 95% 50%)",
    features: [
      { icon: Timer, label: "Pomodoro" },
      { icon: BookCheck, label: "Quizzes" },
      { icon: UsersRound, label: "Groups" },
    ],
  },
  {
    id: "work",
    title: "Work",
    description: "Stay productive with task management and reminders",
    icon: Briefcase,
    color: "hsl(38 92% 50%)",
    features: [
      { icon: CheckSquare, label: "Tasks" },
      { icon: Bell, label: "Reminders" },
    ],
  },
];

export const Modes = () => {
  return (
    <section className="py-20 px-4 relative overflow-hidden" aria-labelledby="modes-title">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" aria-hidden="true" />
      
      <div className="container mx-auto relative z-10">
        <header className="text-center mb-16 animate-fade-in">
          <h2 id="modes-title" className="font-display text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            Choose Your Mode
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Whether you are relaxing, studying, or working, CSW adapts to your needs
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" role="list">
          {modes.map((mode, index) => {
            const Icon = mode.icon;
            return (
              <article
                key={mode.id}
                className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:scale-105 hover:shadow-xl animate-fade-in rounded-lg bg-card"
                style={{ animationDelay: `${index * 0.1}s` }}
                role="listitem"
              >
                {/* Gradient overlay */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                  style={{ background: `linear-gradient(135deg, ${mode.color} 0%, transparent 100%)` }}
                  aria-hidden="true"
                />
                
                <header className="text-center pb-4 p-6">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${mode.color}15` }}
                    aria-hidden="true"
                  >
                    <Icon 
                      className="w-8 h-8 transition-colors duration-300" 
                      style={{ color: mode.color }}
                    />
                  </div>
                  <h3 className="text-2xl font-bold" style={{ color: mode.color }}>
                    {mode.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {mode.description}
                  </p>
                </header>
                
                <div className="px-6 pb-6">
                  <ul className="space-y-3" aria-label={`${mode.title} mode features`}>
                    {mode.features.map((feature) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <li 
                          key={feature.label}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/50 transition-colors duration-200"
                        >
                          <FeatureIcon className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
                          <span className="text-foreground font-medium">{feature.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
